import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';
import { Icon } from './icons';
import BeforeAfterManager from './BeforeAfterManager';

const MAX_SIZE_MB = 10;
const ACCEPT = 'image/jpeg,image/png,image/webp';

export default function ImageManager() {
  const { getIdToken } = useAuth();
  const [heroImages, setHeroImages] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [customerPhotos, setCustomerPhotos] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const fetchImages = useCallback(async () => {
    try {
      const [heroRes, galleryRes, customerRes] = await Promise.all([
        fetch(`${AWS_CONFIG.apiUrl}/images?category=hero`),
        fetch(`${AWS_CONFIG.apiUrl}/images?category=gallery`),
        fetch(`${AWS_CONFIG.apiUrl}/images?category=customer-photos`),
      ]);
      const heroData = await heroRes.json();
      const galleryData = await galleryRes.json();
      const customerData = await customerRes.json();
      setHeroImages(heroData.images || []);
      setGalleryImages(galleryData.images || []);
      setCustomerPhotos(customerData.images || []);
    } catch (err) {
      console.error('Failed to fetch images:', err);
      setError('이미지 목록을 불러오지 못했습니다.');
    }
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const uploadOne = async (category, file, sortOrder, alt) => {
    const token = await getIdToken();
    if (!token) throw new Error('로그인 세션이 만료되었습니다.');

    const presignRes = await fetch(`${AWS_CONFIG.apiUrl}/images/presign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({ category, filename: file.name, contentType: file.type }),
    });
    if (!presignRes.ok) throw new Error(`presign 실패: ${presignRes.status}`);
    const { uploadUrl, imageUrl } = await presignRes.json();

    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!uploadRes.ok) throw new Error(`S3 업로드 실패: ${uploadRes.status}`);

    const putRes = await fetch(`${AWS_CONFIG.apiUrl}/images/${category}/${sortOrder}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({ imageUrl, alt }),
    });
    if (!putRes.ok) throw new Error(`메타 저장 실패: ${putRes.status}`);
  };

  const handleReplace = (category, sortOrder, currentAlt) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPT;
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        alert(`${MAX_SIZE_MB}MB 이하의 이미지만 가능합니다.`);
        return;
      }
      setError('');
      setUploading(`${category}-${sortOrder}`);
      try {
        await uploadOne(category, file, sortOrder, currentAlt);
        await fetchImages();
      } catch (err) {
        setError(`교체 실패: ${err.message}`);
      }
      setUploading(null);
    };
    input.click();
  };

  const handleAdd = (category) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPT;
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      const byCategory = { hero: heroImages, gallery: galleryImages, 'customer-photos': customerPhotos };
      const existing = byCategory[category] || [];
      let nextSort = existing.length > 0
        ? Math.max(...existing.map(i => i.sortOrder || 0)) + 1
        : 1;

      setError('');
      setUploading(`${category}-add`);
      const failed = [];
      for (const file of files) {
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          failed.push(`${file.name} (크기 초과)`);
          continue;
        }
        try {
          const labelMap = { hero: '히어로', gallery: '갤러리', 'customer-photos': '고객님과 함께한 중고차' };
          const alt = `${labelMap[category] || category} ${nextSort}`;
          await uploadOne(category, file, nextSort, alt);
          nextSort += 1;
        } catch (err) {
          failed.push(`${file.name} (${err.message})`);
        }
      }
      await fetchImages();
      setUploading(null);
      if (failed.length > 0) {
        setError(`일부 실패:\n${failed.join('\n')}`);
      }
    };
    input.click();
  };

  const handleDelete = async (category, sortOrder, alt) => {
    if (!window.confirm(`"${alt}" 이미지를 삭제할까요?`)) return;
    setError('');
    setDeleting(`${category}-${sortOrder}`);
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/images/${category}/${sortOrder}`, {
        method: 'DELETE',
        headers: { Authorization: token },
      });
      if (!res.ok) throw new Error(`삭제 실패: ${res.status}`);
      await fetchImages();
    } catch (err) {
      setError(`삭제 실패: ${err.message}`);
    }
    setDeleting(null);
  };

  const renderGrid = (images, category) => (
    <div className="image-manager-grid">
      {images.map(img => {
        const key = `${category}-${img.sortOrder}`;
        const isUploading = uploading === key;
        const isDeleting = deleting === key;
        return (
          <div key={key} className="image-manager-card">
            <div className="image-manager-preview">
              <img src={img.imageUrl} alt={img.alt} />
            </div>
            <div className="image-manager-info">
              <div className="image-manager-label">{img.alt}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="image-manager-btn"
                  disabled={isUploading || isDeleting}
                  onClick={() => handleReplace(category, img.sortOrder, img.alt)}>
                  {isUploading ? '업로드 중...' : '교체'}
                </button>
                <button className="image-manager-btn"
                  disabled={isUploading || isDeleting}
                  onClick={() => handleDelete(category, img.sortOrder, img.alt)}
                  style={{ color: '#ff5252', borderColor: '#ff5252' }}>
                  {isDeleting ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
      <button
        className="image-manager-card image-manager-add"
        onClick={() => handleAdd(category)}
        disabled={uploading === `${category}-add`}
        style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          minHeight: 180, cursor: 'pointer',
          border: '2px dashed var(--border)',
          background: 'transparent', color: 'var(--text-muted)',
          gap: 8, padding: 20,
        }}>
        {uploading === `${category}-add` ? (
          <span>업로드 중...</span>
        ) : (
          <>
            <Icon name="Plus" size={32} />
            <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>이미지 추가</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>여러 장 동시 선택 가능</span>
          </>
        )}
      </button>
    </div>
  );

  return (
    <div>
      <h1>이미지 관리</h1>
      {error && (
        <div className="card" style={{
          padding: 12, marginBottom: 16,
          borderColor: '#ff5252', background: 'rgba(255, 82, 82, 0.08)',
          color: '#ff5252', fontSize: '0.9rem', whiteSpace: 'pre-wrap',
        }}>
          {error}
        </div>
      )}
      <h2 style={{ fontSize: '1.1rem', marginBottom: 16, color: 'var(--text-muted)' }}>
        히어로 이미지 ({heroImages.length}장 · 무제한)
      </h2>
      {renderGrid(heroImages, 'hero')}
      <h2 style={{ fontSize: '1.1rem', margin: '32px 0 16px', color: 'var(--text-muted)' }}>
        갤러리 이미지 ({galleryImages.length}장 · 무제한)
      </h2>
      {renderGrid(galleryImages, 'gallery')}
      <h2 style={{ fontSize: '1.1rem', margin: '32px 0 16px', color: 'var(--text-muted)' }}>
        고객님과 함께한 중고차 ({customerPhotos.length}장 · 무제한)
      </h2>
      {renderGrid(customerPhotos, 'customer-photos')}
      <BeforeAfterManager />
      <p style={{ color: 'var(--text-dim)', fontSize: '0.8rem', marginTop: 24 }}>
        최대 {MAX_SIZE_MB}MB · JPG/PNG/WebP · 히어로는 업로드 순서대로 슬라이드쇼에 표시
      </p>
    </div>
  );
}
